import { Card, CardContent, Stack } from "@mui/material";
import { useSnackbar } from "notistack";
import type React from "react";
import { useState } from "react";
import { useApiMutation } from "../../hooks/use-api-mutation";
import type { ApiConfig, AppConfig } from "../../types";
import AppConfigDialog from "./app-config-dialog";
import { AppConfigTable } from "./app-config-table";

interface ApplicationTabProps {
  data: ApiConfig | undefined;
  isLoading: boolean;
  refetch: () => void;
}

export const ApplicationTab: React.FC<ApplicationTabProps> = ({ data, isLoading, refetch }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState<AppConfig | undefined>();
  const [isEdit, setIsEdit] = useState(false);

  const { enqueueSnackbar } = useSnackbar();

  const { mutate: createConfig, isPending: isCreating } = useApiMutation<
    Partial<AppConfig>,
    AppConfig
  >("POST", "/app-config", {
    onSuccess: (createdConfig) => {
      enqueueSnackbar("Application config created successfully", {
        variant: "success",
        anchorOrigin: {
          horizontal: "right",
          vertical: "bottom",
        },
      });
      // Switch to edit mode with the newly created config
      setEditingConfig(createdConfig);
      setIsEdit(true);
      refetch();
    },
    onError: (error) => {
      enqueueSnackbar(`Failed to create config: ${error.message}`, {
        variant: "error",
        anchorOrigin: {
          horizontal: "right",
          vertical: "bottom",
        },
      });
    },
  });

  const { mutate: updateConfig, isPending: isUpdating } = useApiMutation<
    Partial<AppConfig>,
    AppConfig
  >("PATCH", "", {
    onSuccess: () => {
      enqueueSnackbar("Application config updated successfully", {
        variant: "success",
        anchorOrigin: {
          horizontal: "right",
          vertical: "bottom",
        },
      });
      setDialogOpen(false);
      refetch();
    },
    onError: (error) => {
      enqueueSnackbar(`Failed to update config: ${error.message}`, {
        variant: "error",
        anchorOrigin: {
          horizontal: "right",
          vertical: "bottom",
        },
      });
    },
  });

  const { mutate: deleteConfig, isPending: isDeleting } = useApiMutation<void, void>("DELETE", "", {
    onSuccess: () => {
      enqueueSnackbar("Application config deleted successfully", {
        variant: "success",
        anchorOrigin: {
          horizontal: "right",
          vertical: "bottom",
        },
      });
      refetch();
    },
    onError: (error) => {
      enqueueSnackbar(`Failed to delete config: ${error.message}`, {
        variant: "error",
        anchorOrigin: {
          horizontal: "right",
          vertical: "bottom",
        },
      });
    },
  });

  const handleCreate = () => {
    setEditingConfig(undefined);
    setIsEdit(false);
    setDialogOpen(true);
  };

  const handleEdit = (config: AppConfig) => {
    setEditingConfig(config);
    setIsEdit(true);
    setDialogOpen(true);
  };

  const handleDelete = (config: AppConfig) => {
    deleteConfig({ _url: `/app-config/${config.id}` });
  };

  const handleDialogSubmit = (formData: Partial<AppConfig>) => {
    if (isEdit && editingConfig) {
      updateConfig({ ...formData, _url: `/app-config/${editingConfig.id}` });
    } else {
      createConfig(formData);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingConfig(undefined);
    setIsEdit(false);
  };

  return (
    <Stack direction="column" spacing={2}>
      <Card>
        <CardContent>
          <AppConfigTable
            configs={data?.configs || []}
            isLoading={isLoading}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onCreate={handleCreate}
            isDeleting={isDeleting}
          />
        </CardContent>
      </Card>

      <AppConfigDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        config={editingConfig}
        onSubmit={handleDialogSubmit}
        isLoading={isCreating || isUpdating}
        isEdit={isEdit}
      />
    </Stack>
  );
};
